import React from 'react';
import { FlaskConical, Shield, FileText, Beaker } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { ProductSection, BulkPackagingGrid, InfoCard } from './ProductShared';

const KeyPropertiesTable = ({ properties }) => (
  <ProductSection title="Key Properties" icon={FlaskConical}>
    <Card className="border-2">
      <CardContent className="p-0">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left p-4 font-semibold text-gray-900">Property</th>
              <th className="text-left p-4 font-semibold text-gray-900">Value</th>
            </tr>
          </thead>
          <tbody>
            {properties.map((item) => (
              <tr key={item.property} className="border-b last:border-b-0 hover:bg-amber-50 transition-colors">
                <td className="p-4 font-medium text-gray-700">{item.property}</td>
                <td className="p-4 text-gray-600">{item.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  </ProductSection>
);

const USAGES = [
  { usage: "Refinery Feedstock", desc: "Used as a raw material input for glycerin refining and purification processes" },
  { usage: "Dust Suppression", desc: "Applied on roads and construction sites to control airborne dust particles" },
  { usage: "Wastewater Treatment", desc: "Acts as a carbon source additive to support biological treatment processes" },
  { usage: "Animal Feed Supplement", desc: "Added to livestock feed as an energy-dense supplement improving feed efficiency" },
  { usage: "Industrial Processing", desc: "Utilized in cement grinding, plasticizers, and various chemical manufacturing" },
  { usage: "Biogas Production", desc: "Co-digested in anaerobic digesters to boost methane yield and energy output" }
];

const CrudeGlycerinDetail = ({ product }) => {
  if (!product.keyProperties) return null;

  return (
    <div className="mt-8 space-y-10">
      <KeyPropertiesTable properties={product.keyProperties} />

      <InfoCard icon={Shield} title="Safety & Compliance">
        <p className="text-gray-700 leading-relaxed">{product.safetyInfo}</p>
        <p className="text-sm text-gray-600 mt-3">
          Compliant with major global chemical inventories: EINECS, TSCA, IECSC, and more.
        </p>
      </InfoCard>

      <ProductSection title="Typical Usages" icon={Beaker}>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {USAGES.map((item) => (
            <Card key={item.usage} className="border-2 hover:border-blue-400 transition-colors">
              <CardContent className="p-5">
                <h5 className="font-semibold text-gray-900 mb-2">{item.usage}</h5>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </ProductSection>

      <BulkPackagingGrid packages={product.packaging.bulk} />

      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200">
        <CardContent className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-full mb-4">
            <FileText className="w-7 h-7 text-blue-600" />
          </div>
          <h4 className="text-xl font-bold text-gray-900 mb-2">Technical Documentation</h4>
          <p className="text-gray-700">{product.documentation}</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default CrudeGlycerinDetail;
